import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-follow-widget.st.css';
import {evaluateFunction} from '@wix/devzai-utils-common';
import {
    ButtonType,
    ImageViewResizeMode,
    ListView,
    listViewCreateDataSource,
    ListViewItemRoot,
    RatioMaintainer
} from '@wix/devzai-utils-react';
import {DoppeActionTypeFollow} from '../doppe-action-type-follow';
import {FlexLayout, WixImageView} from '@wix/devzai-common-client';
import {WixImageResource} from '@wix/devzai-common-wix';
import {doppeHideableValueIsVisibleAndNotEqualValue} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeImageIconDeviantArtColored,
    DoppeImageIconFacebookColored,
    DoppeImageIconInstagramColored,
    DoppeImageIconTikTokColored,
    DoppeImageIconTwitchColored,
    DoppeImageIconTwitterColored,
    DoppeImageIconYouTubeColored
} from '../../../doppe-static-assets/doppe-static-assets';
import {
    DoppeActionTypeWidgetHeader
} from '../../../components/doppe-action-type-widget-header/doppe-action-type-widget-header';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {
    DoppeActionTypeWidgetContentSection
} from '../../../components/doppe-action-type-widget-content-section/doppe-action-type-widget-content-section';
import {
    DoppeActionTypeWidgetContentSeparator
} from '../../../components/doppe-action-type-widget-content-separator/doppe-action-type-widget-content-separator';
import {
    DoppeActionTypeWidgetContentLayout
} from '../../../components/doppe-action-type-widget-content-layout/doppe-action-type-widget-content-layout';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';

export const DoppeActionTypeFollowWidget = React.memo(function DoppeActionTypeFollowWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeFollow.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        platform,
        nickName,
        images,
        followers,
        followButtonText,
        followPageLink,
        posts,
        profileImage,
        title,
        description
    } = actionSettings;

    const imagesDataSource = useMemo(() => {

        return listViewCreateDataSource<WixImageResource>(images.slice(0, 6), {
            idFunction: (_item, index) => index
        })
    }, [images]);


    const icon = useMemo(() => {

        switch (platform) {
            case DoppeActionTypeFollow.Platforms.Deviantart:
                return DoppeImageIconDeviantArtColored;
            case DoppeActionTypeFollow.Platforms.Youtube:
                return DoppeImageIconYouTubeColored;
            case DoppeActionTypeFollow.Platforms.Twitter:
                return DoppeImageIconTwitterColored;
            case DoppeActionTypeFollow.Platforms.Instagram:
                return DoppeImageIconInstagramColored;
            case DoppeActionTypeFollow.Platforms.Facebook:
                return DoppeImageIconFacebookColored;
            case DoppeActionTypeFollow.Platforms.Tiktok:
                return DoppeImageIconTikTokColored;
            case DoppeActionTypeFollow.Platforms.Twitch:
                return DoppeImageIconTwitchColored;
            default:
                return undefined;
        }
    }, [platform])

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
        >
            <DoppeActionTypeWidgetHeader
                actionContext={actionContext}
                icon={icon}
                withBorderBottom={false}
            />
            <DoppeActionTypeWidgetContentLayout>
                <DoppeActionTypeWidgetContentSection
                    className={style(classes.linkButtonContainer, {}, FlexLayout.column.default)}
                >
                    {
                        doppeHideableValueIsVisibleAndNotEqualValue(title, '') || doppeHideableValueIsVisibleAndNotEqualValue(description, '') ?
                            <div
                                className={style(classes.textsContentContainer, {}, FlexLayout.column.default)}
                            >
                                <DoppeActionTypeWidgetInnerTitle
                                    className={style(classes.productName, {})}
                                >
                                    {title}
                                </DoppeActionTypeWidgetInnerTitle>
                                <DoppeActionTypeWidgetInnerDescription
                                    className={style(classes.productDescription, {})}
                                >
                                    {description}
                                </DoppeActionTypeWidgetInnerDescription>
                            </div>
                            :
                            null
                    }
                    <DoppeActionTypeWidgetInnerButton
                        className={style(classes.linkButton)}
                        buttonType={ButtonType.NormalButton}
                        tagName={'a'}
                        href={normalizeLinkUrlWithUtmSrcParam(followPageLink)}
                        target={'_blank'}
                        onClick={() => {
                            actionContext.reportGoalAchieved();
                        }}
                    >
                        {followButtonText}
                    </DoppeActionTypeWidgetInnerButton>
                </DoppeActionTypeWidgetContentSection>
            </DoppeActionTypeWidgetContentLayout>
            <DoppeActionTypeWidgetContentSeparator/>
            {
                profileImage || doppeHideableValueIsVisibleAndNotEqualValue(nickName, '') || doppeHideableValueIsVisibleAndNotEqualValue(followers, '') || doppeHideableValueIsVisibleAndNotEqualValue(posts, '') ?
                    <DoppeActionTypeWidgetContentSection>
                        <div
                            className={style(classes.profileDetailsRow, {}, FlexLayout.row.alignToCenter)}
                        >
                            {
                                profileImage ?
                                    <WixImageView
                                        className={style(classes.profileImage, FlexLayout.noShrink)}
                                        src={profileImage}
                                        resizeMode={ImageViewResizeMode.Cover}
                                        positionHorizontal={'center'}
                                        positionVertical={'center'}
                                    />
                                    :
                                    null
                            }
                            {
                                doppeHideableValueIsVisibleAndNotEqualValue(nickName, '') || doppeHideableValueIsVisibleAndNotEqualValue(followers, '') || doppeHideableValueIsVisibleAndNotEqualValue(posts, '') ?

                                    <div
                                        className={style(classes.profileDataContent, {}, FlexLayout.column.default)}
                                    >
                                        {
                                            doppeHideableValueIsVisibleAndNotEqualValue(nickName, '') ?
                                                <div className={style(classes.nickName, {})}>{nickName}</div>
                                                :
                                                null
                                        }
                                        {
                                            doppeHideableValueIsVisibleAndNotEqualValue(followers, '') || doppeHideableValueIsVisibleAndNotEqualValue(posts, '') ?
                                                <div
                                                    className={style(classes.statsDetails, {}, FlexLayout.row.alignToCenter)}>
                                                    {
                                                        doppeHideableValueIsVisibleAndNotEqualValue(followers, '') ?
                                                            <div
                                                                className={style(classes.followers, {})}
                                                            >
                                                                {followers}
                                                            </div>
                                                            :
                                                            null
                                                    }
                                                    {
                                                        doppeHideableValueIsVisibleAndNotEqualValue(posts, '') ?
                                                            <div
                                                                className={style(classes.posts, {})}
                                                            >
                                                                {posts}
                                                            </div>
                                                            :
                                                            null
                                                    }
                                                </div>
                                                :
                                                null
                                        }
                                    </div>
                                    :
                                    null
                            }
                        </div>
                    </DoppeActionTypeWidgetContentSection>
                    :
                    null
            }
            {evaluateFunction(() => {

                if (imagesDataSource.length > 0) {
                    return (
                        <ListView
                            className={style(classes.imagesList, {
                                layout: imagesDataSource.length.toString()
                            })}
                            renderItem={renderProps => {
                                const {
                                    dataItem
                                } = renderProps;

                                return (
                                    <ListViewItemRoot
                                        className={style(classes.listItem, {})}
                                    >
                                        <RatioMaintainer
                                            className={style(classes.imageContainer, {}, FlexLayout.column.default)}
                                            widthHeightRatio={imagesDataSource.length === 1 ? 3 / 2 : 1}
                                        >
                                            <WixImageView
                                                className={style(classes.itemImage, FlexLayout.noShrink)}
                                                src={dataItem}
                                                resizeMode={ImageViewResizeMode.Cover}
                                                positionHorizontal={'center'}
                                                positionVertical={'center'}
                                            />
                                        </RatioMaintainer>
                                    </ListViewItemRoot>
                                )
                            }}
                        >
                            {imagesDataSource}
                        </ListView>
                    )
                } else {
                    return null;
                }
            })}

        </div>
    );
});


function normalizeLinkUrlWithUtmSrcParam(link: string) {
    const utmSrcString = `utm_source=hopp`;

    if (link.indexOf('?') > 0) {
        if (link.indexOf('utm_source') > 0) {
            return link;
        } else {
            return link + `&${utmSrcString}`
        }
    } else {
        return link + `?${utmSrcString}`
    }
}